import { FC } from "react";
import { createPortal } from "react-dom";
import { ICalendarLesson, TLessonStatus } from "../../../../entities/calendar";
import { formatLessonsCount } from "../../../../shared/lib/helpers/formatRuCount";
import { useCalendarEventsPopover } from "../../../../shared/lib/hooks/useCalendarEventsPopover";
import { LongArrowLeftIcon, StudentIcon, TutorIcon } from '../../../../shared/ui/icon'
import { getLessonTransferLabel } from "../../lib/lessonTransfer";
import classes from '../calendarDay.module.scss'

interface IProps {
    lessons: ICalendarLesson[];
    top: number;
    left: number;
    width: number;
    height: number;
    zIndex: number;
    openToLeft: boolean;
    getLessonTimeRange: (lesson: ICalendarLesson) => string;
    getLessonStatusClass: (status: TLessonStatus) => string;
    statusClassName: string;
    onSelectLesson: (lesson: ICalendarLesson) => void;
}

export const CalendarLessonsPopover: FC<IProps> = ({
    lessons,
    top,
    left,
    width,
    height,
    zIndex,
    openToLeft,
    getLessonTimeRange,
    getLessonStatusClass,
    statusClassName,
    onSelectLesson,
}) => {
    const {
        isOpen,
        setIsOpen,
        containerRef,
        popoverRef,
        isMobile,
    } = useCalendarEventsPopover()
    const lessonsCountLabel = formatLessonsCount(lessons.length)

    const lessonsPopover = (
        <section
            ref={popoverRef}
            className={
                `${classes.lessonsPopover} ` +
                (isMobile
                    ? classes.mobileLessonsPopover
                    : openToLeft
                        ? classes.popoverToLeft
                        : classes.popoverToRight
                )
            }
            onPointerDown={event => event.stopPropagation()}
        >
            <section className={classes.popoverHeader}>
                <span className={classes.popoverTitle}>Занятия в это время</span>
                <button
                    type="button"
                    className={classes.closePopoverButton}
                    onClick={() => setIsOpen(false)}
                    aria-label="Закрыть список занятий"
                >
                    ×
                </button>
            </section>
            <section className={classes.popoverList}>
                {lessons.map(lesson => {
                    const lessonTagName = typeof lesson.tag === 'string'
                        ? (lesson.tag || 'Без тега')
                        : (lesson.tag?.name || 'Без тега')
                    const transferLabel = getLessonTransferLabel(lesson)

                    return (
                        <button
                            type="button"
                            key={lesson.id}
                            className={`${classes.popoverLesson} ${getLessonStatusClass(lesson.status)}`}
                            onClick={() => onSelectLesson(lesson)}
                        >
                            <section className={classes.popoverLessonHeader}>
                                <span className={classes.popoverLessonTime}>{getLessonTimeRange(lesson)}</span>
                                <span className={classes.popoverLessonTag}>{lessonTagName}</span>
                            </section>
                            <strong className={classes.popoverStudent}>
                                <StudentIcon size={15} />
                                <span>{lesson.student.fullName}</span>
                            </strong>
                            <span className={classes.popoverDetails}>
                                <TutorIcon size={14} />
                                <span>{lesson.tutor.fullName}</span>
                                <span className={classes.popoverSubject}>{lessonTagName}</span>
                            </span>
                            {transferLabel && (
                                <span className={
                                    classes.popoverLessonTransfer +
                                    (transferLabel.isMovedFrom ? ` ${classes.popoverLessonTransferFrom}` : '')
                                }>
                                    <LongArrowLeftIcon
                                        size={14}
                                        className={
                                            transferLabel.isMovedFrom
                                                ? undefined
                                                : classes.lessonTransferArrowTo
                                        }
                                    />
                                    {transferLabel.text}
                                </span>
                            )}
                        </button>
                    )
                })}
            </section>
        </section>
    )

    return (
        <section
            ref={containerRef}
            className={classes.moreLessons}
            style={{
                top,
                left,
                width,
                maxWidth: `calc(100vw - 76px - ${left}px)`,
                zIndex: isOpen ? 10000 - top : zIndex,
            }}
            onPointerDown={event => event.stopPropagation()}
        >
            <button
                type="button"
                className={`${classes.moreLessonsButton} ${statusClassName}`}
                style={{height}}
                onClick={() => setIsOpen(value => !value)}
                title={`Открыть: ${lessonsCountLabel}`}
            >
                <strong className={classes.moreLessonsCount}>{lessonsCountLabel}</strong>
            </button>
            {isOpen && (
                isMobile
                    ? createPortal(
                        <section
                            className={`${classes.moreLessons} ${classes.mobilePopoverOverlay}`}
                            onPointerDown={() => setIsOpen(false)}
                        >
                            {lessonsPopover}
                        </section>,
                        document.body
                    )
                    : lessonsPopover
            )}
        </section>
    )
}
